import { storeToRefs } from 'pinia'
import {
  useIntegrator,
  type IntegratorParentSSBTConfig
} from '@nsftx/seven-client-retail-vue-integrator'
import { getDeviceAccessToken, useDeviceManagementStore } from '@nsftx/seven-client-retail-vue-dm'
import {
  createSsbtGameConfiguration,
  useGamesStore,
  useTokenPayloadStore
} from '@nsftx/seven-client-retail-vue-sgp'
import { logService, debugService, envService } from '@nsftx/seven-client-retail-utils'
import { getCurrencySymbol } from '@nsftx/seven-nxg-retail-vue-sdk'
import { useGameConfigurationStore } from '@/stores/cms/gameConfigurationStore'
import { integratorSSBTEvents } from './integratorSsbtEventsService'
import integratorSSBTSettingsService from './integratorSsbtSettingsService'
import integratorSSBTBettingService from './integratorSsbtBettingService'
import errorParser from '@/utils/errorParser'

const LOG_PREFIX = '[integratorSSBTService]'

const resolveLinkCodeFromChildId = (childId: string): string | undefined => {
  const { gameLinks } = useGamesStore()
  const { getByGameId } = useGameConfigurationStore()

  const match = gameLinks.find((link) => {
    const { childId: configuredChildId } = createSsbtGameConfiguration(
      getByGameId(link.installation.game.id)
    )
    const resolvedChildId = configuredChildId ?? link.code

    return resolvedChildId === childId || link.code === childId
  })

  return match?.code
}

const initSSBTIntegrator = () => {
  initIntegrator()
}

const initIntegrator = () => {
  const gameStores = useGamesStore()
  const dmStore = useDeviceManagementStore()
  const { getByGameId } = useGameConfigurationStore()
  const { gameLinks } = storeToRefs(gameStores)
  const { appData } = storeToRefs(dmStore)
  const accessToken = getDeviceAccessToken()
  const { baseCurrency } = storeToRefs(useTokenPayloadStore())
  const { createIntegrator } = useIntegrator()

  const env = envService.getEnvironment()
  const debug = debugService.isDebugMode() || envService.isDevelopmentEnv()
  const tenantId = appData.value?.betshop.companyUuid || ''
  const children = gameLinks.value.map((link) => {
    const config = getByGameId(link.installation.game.id)
    const { childId: configuredChildId } = createSsbtGameConfiguration(config)
    const childId = configuredChildId ?? link.code

    return {
      frameId: link.code,
      childId
    }
  })
  const token = accessToken.value || ''
  const activeCurrency = baseCurrency.value || ''

  try {
    const config: IntegratorParentSSBTConfig = {
      app: {
        id: 'ssbt',
        version: '1.0.0',
        displayId: 'SSBT'
      },
      context: {
        tenantId,
        env,
        debug
      },
      children,
      auth: {
        token
      },
      settings: {
        activeCurrency: {
          code: activeCurrency,
          symbol: getCurrencySymbol(activeCurrency)
        },
        activeLocale: 'en',
        oddsOption: 'DECIMAL',
        platformVal: undefined
      },
      env,
      events: integratorSSBTEvents
    }

    createIntegrator(config, accessToken)
    logService.debug(`${LOG_PREFIX} Integrator successfully initialized.`, {
      code: 'ST_INTEGRATOR_INIT_SUCCESS',
      details: {
        children
      }
    })

    integratorSSBTSettingsService.init()
    integratorSSBTBettingService.init()
  } catch (error) {
    logService.error(`${LOG_PREFIX} Failed to initialize integrator.`, {
      code: 'ST_INTEGRATOR_INIT_ERROR',
      ...errorParser.parseUpstream(error)
    })
  }
}

export { initSSBTIntegrator, resolveLinkCodeFromChildId }
